import { useReporteErrores } from '@plataforma/web/errores';
import { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import {
  AvisoPreparacion,
  BarraEstado,
  ContextoPreparacion,
  usePreparacion,
} from '@plataforma/web/Preparacion';
import { useSesion } from '@plataforma/web/sesion';
import { usePuede } from '../permisos';
import { type Entrada, MENU, MENU_AYUDA, MENU_LOTERO, MENU_PORTAL } from './menuPorRol';
import { BarraInferior } from '@plataforma/ui/BarraInferior';
import { Logotipo } from '@plataforma/ui/Logo';
import { SelectorTema } from '@plataforma/ui/SelectorTema';

interface MiLotero {
  nombre: string;
  estado: string;
  tipos: string[];
  modulos: string[];
}
interface Suscripcion {
  estado: string;
  plan: { clave: string; nombre: string };
}

/** Preparación del Admin Lotero (arquitectura 2.25): cada servicio evalúa lo suyo. */
const PREPARACION = [
  '/platform/preparacion',
  '/commerce/preparacion',
  '/channels/preparacion',
  '/workshop/preparacion',
];

/** Enlace del menú con su ícono (lateral y hoja «Más» del teléfono). */
function EnlaceMenu({ m, fin }: { m: Entrada; fin?: boolean }) {
  const Icono = m.icono;
  const { pathname, search } = useLocation();
  // Un destino con filtro (p. ej. Importación = Vehículos en tránsito) solo se marca con su filtro.
  const conFiltro = m.a.includes('?');
  return (
    <NavLink
      to={m.a}
      end={fin ?? m.a === '/'}
      className={({ isActive }) =>
        (conFiltro ? `${pathname}${search}` === m.a : isActive && !search.includes('estado='))
          ? 'active'
          : ''
      }
    >
      <Icono size={18} strokeWidth={1.75} aria-hidden="true" />
      {m.texto}
    </NavLink>
  );
}

const iniciales = (texto: string) =>
  texto
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '·';

export function Marco() {
  const { usuario, salir } = useSesion();
  const api = useApi();
  const { datos: lotero, recargar: recargarLotero } = useConsulta<MiLotero>('/platform/mi-lotero');
  // Registro propio: el lotero queda «pendiente» hasta que su dueño entra con el correo verificado.
  // Al entrar se activa (platform-svc lo exige así y es idempotente); sin esto su tienda nunca abría.
  const estadoLotero = lotero?.estado;
  useEffect(() => {
    if (estadoLotero !== 'pendiente') return;
    api('POST', '/platform/mi-lotero/activar')
      .then(() => recargarLotero())
      .catch(() => {
        // Sin correo verificado o sin rol de dueño: se queda pendiente y se reintenta al entrar.
      });
  }, [estadoLotero]);
  const { datos: suscripcion } = useConsulta<Suscripcion>('/platform/mi-lotero/suscripcion');
  const { pathname } = useLocation();
  const permitido = usePuede();
  // El % y el aviso al entrar, solo para los roles que configuran (dueño y gerente).
  const configura = permitido('usuarios.administrar');
  const preparacion = usePreparacion(configura ? PREPARACION : null);
  const { datos: ia } = useConsulta<{ porcentaje: number | null }>(
    configura ? '/platform/mi-lotero/suscripcion/ia' : null,
  );
  const esProveedor = lotero?.tipos.includes('proveedor') ?? false;
  const soloProveedor = esProveedor && lotero?.tipos.length === 1;
  // 2.27: los errores de esta pantalla van a la bitácora de su negocio.
  useReporteErrores(soloProveedor ? 'portal' : 'admin');
  // Suspendido: lo único abierto es Soporte, la Ayuda, su privacidad y su cuenta.
  const rutaDeAyuda = pathname.startsWith('/ayuda') || pathname === '/mi-cuenta';
  if (lotero?.estado === 'suspendido' && rutaDeAyuda)
    return (
      <div className="contenido">
        <header className="barra">
          <strong>{lotero.nombre} · suspendido</strong>
          <div className="relleno" />
          <SelectorTema />
          <NavLink className="boton" to="/ayuda/tickets">
            Soporte
          </NavLink>
          <NavLink className="boton" to="/ayuda/privacidad">
            Mi privacidad
          </NavLink>
          <button className="boton" onClick={() => void salir()}>
            Salir
          </button>
        </header>
        <main className="pagina">
          <Outlet />
        </main>
      </div>
    );
  if (lotero?.estado === 'suspendido')
    return (
      <div className="centro">
        <div className="entrada">
          <h1>{lotero.nombre} está suspendido</h1>
          <p className="subtitulo">
            Tus datos se conservan, pero por ahora no puedes usar la plataforma. Comunícate con
            soporte para reactivarlo.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <NavLink className="boton primario" to="/ayuda/tickets/nuevo">
              Escribir a Soporte
            </NavLink>
            <button className="boton" onClick={() => void salir()}>
              Salir
            </button>
          </div>
        </div>
      </div>
    );
  if (soloProveedor && !pathname.startsWith('/portal')) return <Navigate to="/portal" replace />;
  const operacion = soloProveedor ? [] : MENU.filter((m) => permitido(m.permiso));
  const delLotero = soloProveedor ? [] : MENU_LOTERO.filter((m) => permitido(m.permiso));
  const portal = esProveedor ? MENU_PORTAL.filter((m) => permitido(m.permiso)) : [];
  const ayuda = MENU_AYUDA.filter((m) => permitido(m.permiso));
  // En el teléfono, los cuatro primeros destinos del rol van en la barra inferior.
  const principales = [...operacion, ...portal].slice(0, 4);
  const menu = (
    <nav className="menu" aria-label="Principal">
      {operacion.map((m) => (
        <EnlaceMenu key={m.texto} m={m} />
      ))}
      {delLotero.length > 0 && (
        <>
          <small className="menu-titulo">Mi lotero</small>
          {delLotero.map((m) => (
            <EnlaceMenu key={m.a} m={m} />
          ))}
        </>
      )}
      {portal.length > 0 && (
        <>
          {!soloProveedor && <small className="menu-titulo">Como proveedor</small>}
          {portal.map((m) => (
            <EnlaceMenu key={m.a} m={m} fin={m.a === '/portal'} />
          ))}
        </>
      )}
      {ayuda.length > 0 && (
        <>
          <small className="menu-titulo">Ayuda</small>
          {ayuda.map((m) => (
            <EnlaceMenu key={m.a} m={m} fin={m.a === '/ayuda'} />
          ))}
        </>
      )}
    </nav>
  );
  return (
    <div className="marco con-barra-inferior">
      <aside className="lateral">
        <Logotipo tamano={26} />
        <div className="lotero">
          <div className="insignia">{iniciales(lotero?.nombre ?? 'S')}</div>
          <div>
            <strong>{lotero?.nombre ?? '…'}</strong>
            <small>
              {suscripcion?.plan
                ? `Plan ${suscripcion.plan.nombre}${suscripcion.estado === 'prueba' ? ' · prueba' : ''}`
                : 'Admin Lotero'}
            </small>
          </div>
        </div>
        {menu}
      </aside>
      <div className="contenido">
        <header className="barra">
          <strong className="solo-movil barra-lotero">{lotero?.nombre}</strong>
          <div className="relleno" />
          <SelectorTema />
          <NavLink className="avatar" to="/mi-cuenta" title="Mi cuenta">
            {iniciales(usuario?.nombre ?? '')}
          </NavLink>
          <div className="solo-escritorio">
            <div>{usuario?.nombre}</div>
            <small className="subtitulo">{usuario?.correo}</small>
          </div>
          <button className="boton" onClick={() => void salir()}>
            Salir
          </button>
        </header>
        <BarraEstado
          preparacion={preparacion}
          equipo={false}
          verPorcentaje={configura}
          consumo={
            configura && suscripcion?.plan
              ? {
                  texto: `Plan ${suscripcion.plan.nombre}${ia?.porcentaje == null ? '' : ` · IA ${ia.porcentaje} %`}`,
                  porcentaje: ia?.porcentaje ?? null,
                  a: '/suscripcion',
                }
              : null
          }
        />
        <main className="pagina">
          {configura && (pathname === '/' || pathname === '/portal') && (
            <AvisoPreparacion preparacion={preparacion} />
          )}
          <ContextoPreparacion.Provider value={preparacion}>
            <Outlet />
          </ContextoPreparacion.Provider>
        </main>
      </div>
      <BarraInferior
        titulo={lotero?.nombre ?? 'Menú'}
        principales={principales.map((m) => {
          const Icono = m.icono;
          return (
            <NavLink
              key={m.a}
              to={m.a}
              end={m.a === '/' || m.a === '/portal'}
              className="ui-barra-inferior-enlace"
            >
              <Icono size={22} strokeWidth={1.75} aria-hidden="true" />
              <span>{m.texto}</span>
            </NavLink>
          );
        })}
        mas={menu}
      />
    </div>
  );
}
